import { replace } from '@xo-union/react-css-modules';
import React, { useRef } from 'react';

import { useMessagingSequenceQuery, useWeddingWebsiteQuery, WeddingWebsiteQuery } from '../../generated/graphql';
import { messagingHubRoute } from '../../helpers/routes';
import { generateMessagingPath } from '../../helpers/generateMessagingPath';
import { Link } from '@xo-union/tk-component-buttons';
import { Link as RouterLink } from 'react-router-dom';
import { ButtonDropdown, ButtonDropdownItem } from '../ButtonDropdown';
import useShareTracking from '../SharePage/useShareTracking';
import { BlockedStateBody, BlockedStateHeader } from './BlockedStateComponents';
import { EmailPreview } from './EmailPreview';
import { Sequences } from './messagingHubHelper';
import styles from './sequencePreview.scss';
import { SmsPreview } from './SmsPreview/SmsPreview';
import {
  SEQUENCE_TYPE_SAVE_THE_DATE,
  SEQUENCE_TYPE_RSVP_REMINDER,
  SEQUENCE_TYPE_ADDRESS_COLLECT,
  SEQUENCE_TYPE_CUSTOM_MESSAGE,
  SEQUENCE_TYPE_SEND_UPDATES,
  SEQUENCE_TYPE_SHARE_DAY_OF_DETAILS,
  SEQUENCE_TYPE_LOCAL_HOTELS,
} from './messagingHubHelper';

interface Props {
  supportedTypes?: Sequences[0]['supportedTypes'];
  wwsData?: WeddingWebsiteQuery;
  displayName?: string;
  sequenceType?: string;
  blocked?: boolean;
}

interface Val {
  title: string;
  subText: string;
  icon: string;
}
export interface Copy {
  email: Val;
  sms: Val;
}

export const copy: Copy = {
  email: {
    title: 'Compose email',
    subText: "We'll email guests your message.",
    icon: 'email',
  },
  sms: {
    title: 'Compose SMS',
    subText: "We'll text guests your message.",
    icon: 'mobile',
  },
};

const SequencePreview: React.FC<Props> = ({ displayName = '', sequenceType = '', supportedTypes, blocked }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { data } = useWeddingWebsiteQuery();
  const emailSeqId = supportedTypes?.find(s => s.type === 'email')?.id;
  const track = useShareTracking();

  const { data: sequence } = useMessagingSequenceQuery({
    variables: { id: emailSeqId! },
    skip: !emailSeqId,
  });

  return (
    <div className={styles.preview}>
      {blocked ? (
        <>
          <BlockedStateHeader />
          <div className={styles.previewBody}>
            <BlockedStateBody />
          </div>
        </>
      ) : (
        <>
          <div className={styles.header}>
            {sequenceType === SEQUENCE_TYPE_SAVE_THE_DATE ? (
              supportedTypes?.map(({ id, type }) => {
                const saveTheDateTo = generateMessagingPath(sequenceType, id, type);

                // Hiding SMS for save the date
                if (type === 'sms') {
                  return null;
                }

                return (
                  <Link
                    as={RouterLink}
                    size="md"
                    color="primary"
                    data-testid="customize-message-button"
                    to={saveTheDateTo}
                    onClick={() => {
                      track('Website Share Initiated', {
                        page: 'messaging hub',
                        action: 'select template',
                        messageMedium: type,
                        messageType: displayName,
                      });
                    }}
                  >
                    Compose email
                  </Link>
                );
              })
            ) : (
              <ButtonDropdown size="md" color="primary" text="Compose" data-testid="customize-message-button">
                {supportedTypes?.map(({ id, type }) => {
                  const to = generateMessagingPath(sequenceType, id, type);

                  // Hiding SMS for save the date
                  // this logic should be cleaned up to be more dynamic
                  if (
                    (sequenceType === 'save-the-date' || sequenceType === SEQUENCE_TYPE_CUSTOM_MESSAGE) &&
                    type === 'sms'
                  ) {
                    return null;
                  }

                  return (
                    <ButtonDropdownItem
                      key={`${id}-${type}`}
                      to={to}
                      onClick={() => {
                        track('Website Share Initiated', {
                          page: 'messaging hub',
                          action: 'select template',
                          messageMedium: type,
                          messageType: displayName,
                        });
                      }}
                      data-testid={`component-${type}-button`}
                      {...copy[type as keyof Copy]}
                    />
                  );
                })}
              </ButtonDropdown>
            )}
          </div>
          <div className={styles.previewBody}>
            {supportedTypes?.map(({ id, type }) => {
              const to = `${messagingHubRoute}/${sequenceType}-${type.toLowerCase()}/${id}/edit`;
              if (type === 'email') {
                return (
                  <EmailPreview
                    key={`${id}-${type}`}
                    iframeRef={iframeRef}
                    match={null}
                    editRoute={to}
                    seqId={id}
                    subject={sequence?.messagingSequence?.defaultEmailSubject || ''}
                    classes={replace({
                      previewFrame: styles.emailPreviewFrame,
                      previewContainer: styles.previewContainer,
                      bodyContainer: styles.iframeContainer,
                      iframe: styles.iframe,
                      header: styles.emailHeader,
                      iframeContainer: styles.emailPreviewScaled,
                      emailPreview: styles.emailPreview,
                    })}
                  />
                );
              } else if (type === 'sms') {
                // Hiding SMS for save the date
                if (sequenceType === SEQUENCE_TYPE_SAVE_THE_DATE) {
                  return null;
                }
                if (sequenceType === SEQUENCE_TYPE_CUSTOM_MESSAGE) {
                  return null;
                }
                // This is kinda ugly but only this two sequences have links and they are both different
                let smsLink = '';
                if (sequenceType === SEQUENCE_TYPE_ADDRESS_COLLECT) {
                  smsLink = `https://${process.env.REACT_APP_HOSTNAME}ac/${data?.weddingWebsite.vanityUrl}`;
                } else if (
                  sequenceType === SEQUENCE_TYPE_RSVP_REMINDER ||
                  sequenceType === SEQUENCE_TYPE_SEND_UPDATES ||
                  sequenceType === SEQUENCE_TYPE_SHARE_DAY_OF_DETAILS ||
                  sequenceType === SEQUENCE_TYPE_LOCAL_HOTELS
                ) {
                  smsLink = `https://${data?.weddingWebsite.wwsUrl}`;
                }
                return (
                  <SmsPreview
                    key={`${id}-${type}`}
                    match={null}
                    editRoute={to}
                    loading={false}
                    values={{
                      greeting: `Sent via The Knot: Hey [guest name], it's ${data?.member?.firstName} & ${data?.member?.fianceFirstName}!`,
                      message: sequence?.messagingSequence?.defaultSmsBody || '',
                      optout: `To unsubscribe from ${data?.member?.firstName} & ${data?.member?.fianceFirstName} reply NO. Msg & data rates apply. To unsubscribe from all couples using The Knot reply STOP.`,
                    }}
                    smsLink={smsLink}
                    classes={replace({
                      previewFrame: styles.smsPreviewFrame,
                      previewContainer: styles.smsPreviewContainer,
                      previewScrollableFrame: styles.smsPreviewScrollableFrame,
                      bodyContainer: styles.smsBodyContainer,
                    })}
                  />
                );
              }

              return null;
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SequencePreview;
